import type { SiteConfig } from "../types";

export interface MatchedClick {
  event_name:
    | "phone_click"
    | "email_click"
    | "whatsapp_click"
    | "telegram_click"
    | "max_click"
    | "booking_click"
    | "outbound_click"
    | "file_download"
    | "cta_click";
  props: Record<string, string | number | boolean | null>;
}

const MESSENGER_PATTERNS: Array<{ test: RegExp; name: "whatsapp_click" | "telegram_click" | "max_click"; messenger: string }> = [
  { test: /(^|\.)(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com|whatsapp\.com)$/i, name: "whatsapp_click", messenger: "whatsapp" },
  { test: /(^|\.)(t\.me|telegram\.me|telegram\.org)$/i, name: "telegram_click", messenger: "telegram" },
  // MAX (VK's messenger) — share links look like https://max.ru/u/<token>
  { test: /(^|\.)max\.ru$/i, name: "max_click", messenger: "max" },
];

const BOOKING_PATTERNS: Array<{ host?: RegExp; href?: RegExp; provider: string }> = [
  { host: /(^|\.)yclients\.com$/i, provider: "yclients" },
  { host: /(^|\.)n\d+\.yclients\.com$/i, provider: "yclients" },
  { host: /(^|\.)dikidi\.(ru|net)$/i, provider: "dikidi" },
  { host: /(^|\.)n\d+\.dikidi\.(ru|net)$/i, provider: "dikidi" },
  // Yandex Afisha ticket widget (events / sessions pages) — the real purchase
  // funnel for venues that sell via Afisha; without this it is just outbound.
  { host: /^widget\.afisha\.yandex\.ru$/i, provider: "yandex_afisha" },
  { host: /(^|\.)afisha\.yandex\.ru$/i, provider: "yandex_afisha" },
  { href: /\/booking(\b|\/)/i, provider: "unknown" },
  { href: /[?&]action=book(ing)?\b/i, provider: "unknown" },
];

function hrefHost(href: string, currentHost: string): string | null {
  try {
    return new URL(href, "http://" + currentHost).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function hostInAllowed(host: string, allowed: string[]): boolean {
  const h = host.toLowerCase().replace(/^\./, "");
  for (const a of allowed) {
    const allowedHost = a.toLowerCase().replace(/^\./, "");
    if (h === allowedHost || h.endsWith("." + allowedHost)) return true;
  }
  return false;
}

/**
 * Resolve a clicked anchor into a tracked click event, if any.
 * Order matters: tel/mailto → messengers → booking → custom_goals → outbound.
 * Returns null if the click should not be tracked.
 */
export function matchAnchorClick(
  anchor: HTMLAnchorElement,
  config: SiteConfig | null,
  currentHost: string,
): MatchedClick | null {
  const rawHref = anchor.getAttribute("href") || "";
  if (!rawHref) return null;

  const lower = rawHref.toLowerCase();

  if (lower.startsWith("tel:")) {
    return { event_name: "phone_click", props: { href_type: "tel" } };
  }
  if (lower.startsWith("mailto:")) {
    return { event_name: "email_click", props: { href_type: "mailto" } };
  }

  const host = hrefHost(rawHref, currentHost);

  if (host) {
    for (const p of MESSENGER_PATTERNS) {
      if (p.test.test(host)) {
        return { event_name: p.name, props: { messenger: p.messenger } };
      }
    }

    for (const p of BOOKING_PATTERNS) {
      if (p.host && p.host.test(host)) {
        return { event_name: "booking_click", props: { booking_provider: p.provider } };
      }
      if (p.href && p.href.test(rawHref)) {
        return { event_name: "booking_click", props: { booking_provider: p.provider } };
      }
    }
  }

  // Custom goals: CSS selectors mapped to event_name.
  if (config && config.custom_goals && config.custom_goals.length > 0) {
    for (const goal of config.custom_goals) {
      if (!goal || !goal.selector) continue;
      try {
        if (anchor.matches(goal.selector)) {
          const eventName = goal.event_name as MatchedClick["event_name"];
          const props: MatchedClick["props"] = { selector: goal.selector.slice(0, 256) };
          const id = anchor.id;
          if (id) props.selector_id = id.slice(0, 128);
          if (eventName === "cta_click") props.cta_type = "primary";
          return { event_name: eventName, props };
        }
      } catch {
        // invalid selector — ignore
      }
    }
  }

  // File download: a link to a document (catalog, price list) is an action in
  // its own right — B2B sites often have no other call to action on price pages.
  const fileMatch = rawHref.split(/[?#]/)[0].match(/\.(pdf|xlsx?|docx?|pptx?|zip|rar)$/i);
  if (fileMatch) {
    const name = rawHref.split(/[?#]/)[0].split("/").pop() || "";
    return { event_name: "file_download", props: { file_ext: fileMatch[1].toLowerCase(), file_name: name.slice(0, 128) } };
  }

  // Outbound: link to a domain not in the site's allowed list.
  if (host && !hostInAllowed(host, config?.allowed_domains ?? [currentHost])) {
    const path = (() => {
      try {
        return new URL(rawHref, "http://" + currentHost).pathname.slice(0, 256);
      } catch {
        return null;
      }
    })();
    return {
      event_name: "outbound_click",
      props: { target_domain: host.slice(0, 128), target_path: path },
    };
  }

  return null;
}

/**
 * Walk up the DOM to find the closest anchor or button. Returns null if none.
 */
export function closestClickable(node: EventTarget | null): HTMLAnchorElement | HTMLButtonElement | null {
  let cur = node as Node | null;
  while (cur && cur.nodeType !== 9) {
    if (cur instanceof HTMLAnchorElement || cur instanceof HTMLButtonElement) return cur;
    cur = (cur as Node).parentNode;
  }
  return null;
}

/**
 * Try to match a non-anchor clickable (button) against custom_goals only.
 */
export function matchButtonClick(
  button: HTMLButtonElement,
  config: SiteConfig | null,
): MatchedClick | null {
  if (!config || !config.custom_goals) return null;
  for (const goal of config.custom_goals) {
    if (!goal || !goal.selector) continue;
    try {
      if (button.matches(goal.selector)) {
        const eventName = goal.event_name as MatchedClick["event_name"];
        const props: MatchedClick["props"] = { selector: goal.selector.slice(0, 256) };
        const id = button.id;
        if (id) props.selector_id = id.slice(0, 128);
        if (eventName === "cta_click") props.cta_type = "primary";
        return { event_name: eventName, props };
      }
    } catch {
      // ignore
    }
  }
  return null;
}
